import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Skeleton, Typography } from '@mui/material';
import Avatar from '@sharedCR/common/Avatar';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import { runInPendingChangesOperation } from 'app/services/pendingChangesOperation';
import { NoAvailableMessageContent } from 'app/shared/components/common/NoAvailableMessage';
import { CUSTOM_FIELDS_PARTIAL_SAVE_MESSAGE } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import { buildCustomFieldsPendingChangesNode } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.utils';
import useCustomFieldsValidation from 'app/shared/components/customFields/CustomFieldsSection/hooks/useCustomFieldsValidation';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import usePrevious from 'hooks/usePrevious';
import useRemoteFieldValueSync from 'hooks/useRemoteFieldValueSync';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import { getPersonInitials } from 'utils/loanApplication';
import { getConfigInfo } from 'utils/loanManagement';
import { fillPendingChangesWithActionType } from 'utils/pendingChanges';

import { showMessageAction } from 'store/global/actions';
import { selectThemeCoreDesignSystem } from 'store/global/selectors';
import {
  selectFinancialInfoBorrowers,
  selectFinancialInfoPendingChanges,
  selectIsAdditionalLoanPurposeFromApplication,
  selectIsChattelLoanPurposeFromApplication,
  selectIsRefinanceLoanPurposeFromApplication,
} from 'store/loanApplications/selectors';

import {
  FINANCIAL_INFO_TABLE_KEY,
  FINANCIAL_INFO_VIEWS_PATHS,
  NEW_RECORD_DATA,
} from '../../FinancialInfo.constants';

import { CONTENT_DATA, CUSTOM_FIELDS_TARGET_BY_TABLE_PATH } from './Content.constants';
import {
  borrowerNameStyles,
  borrowersStyles,
  borrowerStyles,
  getBorrowersContainerStyles,
  getContentStyles,
  getWrapperStyles,
  sectionWrapperStyles,
  separatorStyles,
} from './Content.styles';
import {
  clearEntitiesSideDataFromEmptyData,
  getValidationErrorSummary,
  mergeDataWithPendingChanges,
  mergeValues,
  runSaveValidation,
} from './Content.utils';
import useContentFormChanged from './hooks/useContentFormChanged';
import RowCustomFields from './RowCustomFields';
import { PROPERTY_MODEL_TYPE } from './SectionContent/Reo/Reo.constants';

const Content = ({
  setIsFormChanged,
  isSaving,
  isLoading,
  setIsLoading,
  onClose,
  isData,
  setIsContentLoading,
  setOnSave,
  additionalProps: {
    deletedRecords,
    deletedInitialRecords,
    newRecords,
    updateNewRecordOwners,
    newAddedInitialRecords,
    onCountPendingChanges,
    setPendingChanges,
    onSaveBlocked,
    onValidationErrorSummaryChange,
  },
  setIsSavingAllowed,
  setIsNestedEntityEditActive,
}) => {
  const dispatch = useDispatch();
  const {
    applicationId,
    subtab: tablePath,
    subsection: recordId,
    entityId: borrowerId,
  } = useParams();

  const rawPendingChanges = useSelector(selectFinancialInfoPendingChanges)[
    FINANCIAL_INFO_TABLE_KEY[tablePath]
  ];

  const [isError, setIsError] = useState(false);
  const [data, setData] = useState({});
  const [rawData, setRawData] = useState({});
  const [config, setConfig] = useState({ groups: [] });
  const [isOwnersClicked, setIsOwnersClicked] = useState(false);
  const [recordsWithTouchedOwners, setRecordsWithTouchedOwners] = useState([]);
  const [formattedPendingChanges, setFormattedPendingChanges] = useState(rawPendingChanges);
  const [entitiesSideData, setEntitiesSideData] = useState({});
  const [initialEntitiesSideData, setInitialEntitiesSideData] = useState({});
  const [isSectionSavingAllowed, setIsSectionSavingAllowed] = useState(true);

  const formDataRef = useRef({});

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const borrowers = useSelector(selectFinancialInfoBorrowers);
  const theme = useSelector(selectThemeCoreDesignSystem);
  const isRefinanceFlow = useSelector(selectIsRefinanceLoanPurposeFromApplication);
  const isChattelLoan = useSelector(selectIsChattelLoanPurposeFromApplication);
  const isAdditionalLoan = useSelector(selectIsAdditionalLoanPurposeFromApplication);
  const isAssetTypeSourceOfGrantEnabled = useFeatureToggle(FeatureFlag.EnableSourceOfGrant);

  const recordData = data?.[recordId];
  const isNewRecord = recordId?.startsWith(NEW_RECORD_DATA.id);
  const isNewRecordBeforeRedirect = recordId === NEW_RECORD_DATA.id;
  const isDeleted = deletedRecords.has(recordId) || deletedInitialRecords.has(recordId);
  const ownersPendingChanges = formattedPendingChanges?.[recordId]?.owners;

  const relatedBorrowers = useMemo(
    () =>
      borrowers.reduce((acc, borrower) => {
        const isOwner = data?.[recordId]?.owners
          ? data[recordId].owners.includes(borrower.id)
          : borrower.id === data[recordId]?.owner;

        if (!isOwner) {
          return acc;
        }

        const isChanged =
          !ownersPendingChanges?.initialValue?.includes(borrower.id) &&
          ownersPendingChanges?.newValue?.includes(borrower.id);

        acc.push({
          ...borrower,
          isChanged,
        });

        return acc;
      }, []),
    [borrowers, data, ownersPendingChanges, recordId],
  );
  const isOwnersOnlyViewData =
    tablePath === FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT ||
    (tablePath === FINANCIAL_INFO_VIEWS_PATHS.REO &&
      recordData?.type === PROPERTY_MODEL_TYPE.SUBJECT_PROPERTY);
  const prevNewRecords = usePrevious(newRecords);

  const {
    getDataAction,
    SectionContent,
    normalizeReceivedData,
    normalizeDataToSend,
    normalizePendingChanges,
    sendDataAction,
    tabId,
  } = CONTENT_DATA[tablePath];

  const isReo = tablePath === FINANCIAL_INFO_VIEWS_PATHS.REO;
  const customFieldsTarget = CUSTOM_FIELDS_TARGET_BY_TABLE_PATH[tablePath];
  const isCustomFieldsEnabled = !!customFieldsTarget && !isNewRecord && !isDeleted;

  const customFieldsController = useCustomFieldsValidation({
    applicationId: isCustomFieldsEnabled ? applicationId : null,
    target: customFieldsTarget,
    targetEntityId: isCustomFieldsEnabled ? recordId : null,
  });
  const { isValid: isCustomFieldsSaveAllowed, save: customFieldsSave } = customFieldsController;
  const hasDirtyCustomFields = !_.isEmpty(customFieldsController.dirtyValues);

  const dataWithPendingChanges = useMemo(
    () => mergeDataWithPendingChanges(data, rawPendingChanges),
    [data, rawPendingChanges],
  );

  const { initialFormValues, schema, fieldsConfig, observableFieldsNames } = useMemo(() => {
    return getConfigInfo({
      applicationData: dataWithPendingChanges,
      config,
      isNestedForm: true,
    });
  }, [dataWithPendingChanges, config]);

  const {
    form,
    handleChange,
    validationErrors,
    validationErrorsRef,
    setSchema,
    setInForm,
    setMultipleInForm,
    isFormChanged: isFormDirty,
    validateField,
    validateForm,
    setInitialFormValues,
    setValidationErrors,
    staticValidateField,
    setForm,
    setExcludedValidationPaths,
    formPendingChanges,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const { remotePendingChanges, getCleanFormForSave, remoteInjectedFormKeys } =
    useRemoteFieldValueSync({
      config,
      isNestedForm: true,
      setForm,
      setInitialFormValues,
    });

  useEffect(() => {
    setExcludedValidationPaths(remoteInjectedFormKeys);
  }, [remoteInjectedFormKeys, setExcludedValidationPaths]);

  const allDeletedRecords = useMemo(
    () => [...Array.from(deletedRecords), ...Array.from(deletedInitialRecords)],
    [deletedRecords, deletedInitialRecords],
  );

  const allNewRecords = useMemo(
    () => [...Array.from(newAddedInitialRecords), ...newRecords.map(({ id }) => id)],
    [newAddedInitialRecords, newRecords],
  );

  const recordOwners = useMemo(
    () =>
      form[recordId]?.owners ?? [
        ...getConditionalArray(form[recordId]?.owner, [form[recordId]?.owner]),
      ],
    [form, recordId],
  );

  useContentFormChanged({
    deletedRecords,
    entitiesSideData,
    hasDirtyCustomFields,
    initialEntitiesSideData,
    isFormDirty,
    newRecords,
    setIsFormChanged,
  });

  useEffect(() => {
    setEntitiesSideData(clearEntitiesSideDataFromEmptyData);
  }, [borrowerId, recordId, isDeleted]);

  useEffect(() => {
    const isOwnersTouched = recordsWithTouchedOwners.includes(recordId);

    setIsOwnersClicked(isOwnersTouched);
  }, [recordId, recordsWithTouchedOwners]);

  useEffect(() => {
    if (!isNewRecord || !recordOwners) {
      return;
    }

    updateNewRecordOwners(
      recordId,
      recordOwners.map(({ value }) => value),
    );
  }, [updateNewRecordOwners, recordOwners, recordId, isNewRecord]);

  useEffect(() => {
    const normalizedPendingChanges = normalizePendingChanges({
      initialPendingChanges: rawPendingChanges,
      formPendingChanges,
      data,
      form,
      isAssetTypeSourceOfGrantEnabled,
      isEditFlow: true,
      entitiesSideData,
      initialEntitiesSideData,
    });

    const filledPendingChangesWithActionType = fillPendingChangesWithActionType({
      pendingChanges: normalizedPendingChanges,
      deletedRecords: allDeletedRecords,
      newRecords: allNewRecords,
      newAddedInitialRecords: Array.from(newAddedInitialRecords),
    });

    const mergedWithRemote = _.merge({}, filledPendingChangesWithActionType, remotePendingChanges);
    const customFieldsNode = buildCustomFieldsPendingChangesNode(
      customFieldsController.pendingChangesByDefinition,
    );
    const mergedWithCustomFields = _.merge(
      {},
      mergedWithRemote,
      getConditionalObject(isCustomFieldsEnabled && !!recordId, () => ({
        [recordId]: { customFields: customFieldsNode },
      })),
    );

    setFormattedPendingChanges(mergedWithCustomFields);
    onCountPendingChanges(mergedWithCustomFields);
    setPendingChanges(mergedWithCustomFields);
  }, [
    data,
    onCountPendingChanges,
    rawPendingChanges,
    formPendingChanges,
    normalizePendingChanges,
    setPendingChanges,
    form,
    allDeletedRecords,
    allNewRecords,
    isAssetTypeSourceOfGrantEnabled,
    newAddedInitialRecords,
    entitiesSideData,
    initialEntitiesSideData,
    remotePendingChanges,
    customFieldsController.pendingChangesByDefinition,
    isCustomFieldsEnabled,
    recordId,
  ]);

  const setRecordConfig = useCallback((config, recordId) => {
    setConfig(prev => ({
      // eslint-disable-next-line no-unsafe-optional-chaining
      groups: [...prev?.groups.filter(({ key }) => !key.startsWith(recordId)), ...config.groups],
    }));
  }, []);

  useEffect(() => {
    setData(prev => {
      if (!newRecords.length) {
        return prev;
      }

      return { ...prev, ..._.keyBy(newRecords, 'id') };
    });
  }, [newRecords]);

  useEffect(() => {
    setConfig(prev => {
      const { groups, ...restConfigFields } = prev;

      const updatedGroups = groups.filter(group => {
        const groupId = group.key.slice(0, allDeletedRecords[0]?.length);

        return groupId.startsWith(NEW_RECORD_DATA.id)
          ? !!newRecords.find(({ id: newRecordId }) => groupId.startsWith(newRecordId))
          : !allDeletedRecords.includes(groupId);
      });

      return {
        ...restConfigFields,
        groups: updatedGroups,
      };
    });
  }, [newRecords, allDeletedRecords]);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    setIsContentLoading(true);
    setIsError(false);

    dispatch(
      getDataAction({
        applicationId,
        ...getConditionalObject(isReo && (isRefinanceFlow || isAdditionalLoan), {
          withSubjectProperty: true,
        }),
      }),
    )
      .then(data => {
        const { formattedData, rawData } = normalizeReceivedData(data);

        setData(formattedData);
        setRawData(rawData);
      })
      .catch(() => setIsError(true))
      .finally(() => {
        setIsContentLoading(false);
        setIsLoading(false);
      });
  }, [
    applicationId,
    getDataAction,
    normalizeReceivedData,
    dispatch,
    setIsLoading,
    setIsContentLoading,
    isReo,
    isRefinanceFlow,
    isAdditionalLoan,
  ]);

  useEffectSafeStrictMode(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  const isFormDirtyRef = useRef(false);

  useEffect(() => {
    isFormDirtyRef.current = isFormDirty;
  }, [isFormDirty]);

  useEffect(() => {
    setInitialFormValues(initialFormValues);
    setForm(form =>
      mergeValues({
        form,
        initialValues: initialFormValues,
        isFormDirty: isFormDirtyRef.current,
      }),
    );
  }, [initialFormValues, setForm, setInitialFormValues]);

  useEffect(() => {
    formDataRef.current = form;
  }, [form, data, recordId]);

  useEffect(() => {
    onValidationErrorSummaryChange(
      getValidationErrorSummary({
        errors: validationErrors,
        form,
        data,
        deletedRecordIds: allDeletedRecords,
      }),
    );
  }, [onValidationErrorSummaryChange, validationErrors, form, data, allDeletedRecords]);

  useEffect(() => {
    if (!deletedRecords.size) {
      return;
    }

    setValidationErrors(prev =>
      Object.entries(prev).reduce((acc, [recordId, value]) => {
        if (!deletedRecords.has(recordId)) {
          acc[recordId] = value;
        }

        return acc;
      }, {}),
    );
  }, [setValidationErrors, deletedRecords]);

  useEffect(() => {
    if (prevNewRecords?.length <= newRecords.length) {
      return;
    }

    const deletedNewRecordsIds = prevNewRecords?.reduce((acc, { id }) => {
      if (!newRecords.find(newRecord => newRecord.id === id)) {
        acc.push(id);
      }

      return acc;
    }, []);

    setValidationErrors(prev =>
      Object.entries(prev).reduce((acc, [recordId, value]) => {
        if (!deletedNewRecordsIds.includes(recordId)) {
          acc[recordId] = value;
        }

        return acc;
      }, {}),
    );
  }, [prevNewRecords, newRecords, setValidationErrors]);

  const onSave = useCallback(async () => {
    const isValid = await runSaveValidation({
      validateForm,
      validationContext: { noFormChangedValidate: true, ...formDataRef.current },
      getErrors: () => validationErrorsRef.current,
      fieldsConfig,
      onSaveBlocked,
    });

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    const normalizedDataToSend = normalizeDataToSend({
      dataToNormalize: getCleanFormForSave(formDataRef.current),
      data,
      rawData,
      deletedRecords,
      isRefinanceFlow,
      isChattelLoan,
      entitiesSideData,
    });

    return runInPendingChangesOperation(() =>
      dispatch(
        sendDataAction({
          applicationId,
          ...normalizedDataToSend,
          requestMetadata: {
            tabId,
          },
        }),
      ).then(async () => {
        if (isCustomFieldsEnabled) {
          try {
            await customFieldsSave();
          } catch {
            dispatch(
              showMessageAction({
                message: CUSTOM_FIELDS_PARTIAL_SAVE_MESSAGE,
                variant: 'error',
              }),
            );

            return;
          }
        }

        setTimeout(onClose);
      }),
    )
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    entitiesSideData,
    dispatch,
    setIsLoading,
    validateForm,
    validationErrorsRef,
    fieldsConfig,
    onSaveBlocked,
    getCleanFormForSave,
    applicationId,
    sendDataAction,
    onClose,
    normalizeDataToSend,
    deletedRecords,
    data,
    rawData,
    tabId,
    isRefinanceFlow,
    isChattelLoan,
    isCustomFieldsEnabled,
    customFieldsSave,
  ]);

  useEffect(() => {
    setOnSave(() => onSave);
  }, [setOnSave, onSave]);

  useEffect(() => {
    setIsSavingAllowed(
      isSectionSavingAllowed && (!isCustomFieldsEnabled || isCustomFieldsSaveAllowed),
    );
  }, [
    setIsSavingAllowed,
    isSectionSavingAllowed,
    isCustomFieldsEnabled,
    isCustomFieldsSaveAllowed,
  ]);

  const onOwnersClick = useCallback(() => {
    if (isOwnersOnlyViewData) {
      return;
    }

    setIsOwnersClicked(true);
    setRecordsWithTouchedOwners(prev => [...prev, recordId]);
  }, [isOwnersOnlyViewData, recordId]);

  return (
    <Box sx={getWrapperStyles(theme)}>
      <ErrorHandlingLayout isError={isError} onClick={fetchData}>
        <Box sx={getContentStyles(isDeleted)}>
          {isLoading && !isSaving && !isNewRecord && !isOwnersClicked ? (
            <Skeleton height={33} width={160} />
          ) : (
            <>
              {isData && !isDeleted && !isNewRecord && !isOwnersClicked && (
                <Box
                  sx={getBorrowersContainerStyles({ isActive: !isOwnersOnlyViewData })}
                  onClick={onOwnersClick}
                >
                  {relatedBorrowers.map(
                    ({ id, firstName, lastName, fullName, modifiedFields, isChanged }, index) => (
                      <Box key={id} sx={borrowersStyles}>
                        <Box sx={borrowerStyles}>
                          <Avatar initials={getPersonInitials({ firstName, lastName })} />
                          <PendingChangeWrapper
                            isSmall
                            pendingChangesActionType={
                              modifiedFields.includes('BorrowerFullName') || isChanged
                                ? PendingChangesActionType.MODIFIED
                                : undefined
                            }
                          >
                            <Typography sx={borrowerNameStyles} variant="regular14">
                              {fullName}
                            </Typography>
                          </PendingChangeWrapper>
                        </Box>

                        {index + 1 < relatedBorrowers.length && (
                          <Typography sx={separatorStyles} variant="regular14">
                            &
                          </Typography>
                        )}
                      </Box>
                    ),
                  )}
                </Box>
              )}
            </>
          )}

          <Box
            sx={sectionWrapperStyles}
            onMouseLeave={onResetTooltipState}
            onMouseOver={onMouseOver}
          >
            <TruncatedProvider />

            {isData && !isNewRecordBeforeRedirect && !isDeleted ? (
              <>
                <SectionContent
                  allDeletedRecords={allDeletedRecords}
                  applicationId={applicationId}
                  borrowerId={borrowerId}
                  data={data}
                  disabledPendingChangesHighlight={
                    (isLoading && !isSaving) || isNewRecord || newAddedInitialRecords.has(recordId)
                  }
                  entitiesSideData={entitiesSideData}
                  form={form}
                  formDataRef={formDataRef}
                  handleChange={handleChange}
                  initialEntitiesSideData={initialEntitiesSideData}
                  initialFormValues={initialFormValues}
                  isDeleted={isDeleted}
                  isLoading={isLoading}
                  isNewRecord={isNewRecord}
                  isOwnersClicked={isOwnersClicked}
                  isSaving={isSaving}
                  newRecords={newRecords}
                  pendingChanges={formattedPendingChanges}
                  recordId={recordId}
                  schema={schema}
                  setEntitiesSideData={setEntitiesSideData}
                  setForm={setForm}
                  setInForm={setInForm}
                  setInitialEntitiesSideData={setInitialEntitiesSideData}
                  setIsLoading={setIsLoading}
                  setIsNestedEntityEditActive={setIsNestedEntityEditActive}
                  setIsSavingAllowed={setIsSectionSavingAllowed}
                  setMultipleInForm={setMultipleInForm}
                  setRecordConfig={setRecordConfig}
                  setSchema={setSchema}
                  setValidationErrors={setValidationErrors}
                  staticValidateField={staticValidateField}
                  validateField={validateField}
                  validationErrors={validationErrors}
                />
                {isCustomFieldsEnabled && (
                  <RowCustomFields
                    applicationId={applicationId}
                    customFieldsController={customFieldsController}
                    target={customFieldsTarget}
                    targetEntityId={recordId}
                  />
                )}
              </>
            ) : (
              <NoAvailableMessageContent message="Sorry but we could not find data for this borrower(s)" />
            )}
          </Box>
        </Box>
      </ErrorHandlingLayout>
    </Box>
  );
};

Content.propTypes = {
  additionalProps: PropTypes.object.isRequired,
  isData: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  setOnSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  ...WithLoadingPropTypes,
};

export default Content;
